import { Headline, Text, TextLink } from '@oneaudi/unified-web-components';
import { ReactNode } from 'react';
import { useI18n } from '~/app/i18n/i18n';
import { TechnicalDataEntryWrapper } from '~/app/pages/configurator/components/technical-data-dialog/components/technical-data-dialog-content/components/technical-data-sections/styles/technical-data-sections-style';
import { DataSection, DataSectionEntry } from '~/app/utils/data-sections';
import { useFootnotes } from '~/components/footnotes';
import { FootnoteReferenceNgc } from '~/components/footnotes/footnote-reference';

export function TechnicalDataSections({ sections, children }: { sections: DataSection[]; children?: ReactNode }) {
	const { i18n } = useI18n();

	return (
		<ul className="flex flex-col gap-16">
			{sections.map((section) => {
				return (
					<li key={section.title}>
						{section.title === i18n('ngc.technicalData.consumptionAndEmission') && children}
						<TechnicalDataSection key={section.title} title={section.title} entries={section.entries} />
					</li>
				);
			})}
		</ul>
	);
}

function TechnicalDataSection({ title, entries }: DataSection) {
	const { i18n } = useI18n();
	const caeTitle = i18n('ngc.technicalData.consumptionAndEmission');
	const wltpUrl = i18n('ngc.technicalData.wltpUrl');

	return (
		<div className="flex flex-col">
			{/* eslint-disable-next-line */}
			<div tabIndex={0} className="flex w-full justify-between border-b-2 border-black pb-2">
				<Headline tag="label" variant={{ type: 'headline', order: '3', weight: 'normal' }}>
					{title}
				</Headline>
				{title === caeTitle && wltpUrl && (
					<TextLink size="md" variant={'standalone'} href={wltpUrl} target="_blank">
						{i18n('ngc.technicalData.wltpLink')}
					</TextLink>
				)}
			</div>

			<ul className="flex flex-col">
				{entries.map((entry, index) => (
					<TechnicalDataEntry key={index} entry={entry} />
				))}
			</ul>
		</div>
	);
}

function TechnicalDataEntry({ entry }: { entry: DataSectionEntry }) {
	const { addFootnote, getReference } = useFootnotes();
	const { i18n } = useI18n();

	if (entry.remarks && entry.remarks[0] && entry.remarks[0].value) {
		addFootnote(entry.remarks[0].id, entry.remarks[0].value);
	}

	return (
		<TechnicalDataEntryWrapper tabIndex={0}>
			<div className="technical-item">
				<Text variant={{ type: 'copy', order: '1', style: 'bold' }}>
					<span dangerouslySetInnerHTML={{ __html: entry.title }} />

					<FootnoteReferenceNgc
						reference={getReference(entry.remarks?.at(0)?.id ?? '')}
						ariaLabel={i18n('ngc.a11y.footnotes.technicalData', {
							technicalDataItem: entry.title,
						}).replace(/<\/?sub>/g, '')}
					/>
				</Text>
			</div>

			<div className="technical-item">
				<Text variant={{ type: 'copy', order: '1', style: 'normal' }}>
					<span dangerouslySetInnerHTML={{ __html: entry.value }} />
				</Text>
			</div>
		</TechnicalDataEntryWrapper>
	);
}
